import { API_BASE } from './api-base';
import { apiFetch } from './api-client';

/**
 * The chip the user tapped, held until they actually SEND something.
 *
 * Tapping a suggested-task chip only prefills the composer — the user still presses send, and may
 * instead reword it, clear it, or walk away. So the tap is not the act; the send is. This module holds
 * the label between the two and reports it exactly once, when a message really goes out.
 *
 * ## Why a module-scope variable and not React state
 *
 * The flush has to happen at the ONE place every send passes through, which is `use-chat`'s `send` —
 * not `MessagesPanel`'s `submitMessage`, which is only one of four ways a message leaves this app (the
 * home-launcher autosend, a voice note, and a choice-chip answer all reach `send` without it). Threading
 * a "which chip prefilled this" argument through all four call sites would mean four chances to forget
 * one, and three of them have nothing to do with chips. The chip row and the composer are a single
 * instance per page, so one variable is the honest model of it.
 *
 * ## What it is not
 *
 * Not analytics — the chip already logs its own BI on tap. This is a durable "do not suggest this again"
 * ledger entry, and it is deliberately recorded on the send rather than on the tap so that a chip the
 * user considered and rejected stays in the row.
 */

let pendingLabel: string | undefined;

/** The user tapped a chip. Last tap wins: they changed their mind about which one to send. */
export function parkSuggestedTask(label: string): void {
  pendingLabel = label.trim() || undefined;
}

/**
 * The composer was emptied without a send — the user declined the suggestion, so nothing is recorded.
 *
 * Called on the composer going empty rather than on every keystroke: EDITING a prefilled prompt is
 * still acting on the chip (the whole point is that the text is a draft), and only clearing it out is a
 * decision not to send it.
 */
export function dropSuggestedTask(): void {
  pendingLabel = undefined;
}

/**
 * A message is being sent. If a chip prefilled it, record that and forget it.
 *
 * Fire-and-forget on purpose. The send is the user's action and must not wait on, or be failed by, our
 * bookkeeping about which chip started it — the route answers 200 even when it records nothing, and a
 * network failure here costs one repeated chip. The pending label is cleared BEFORE the request so a
 * second send in the same session cannot double-report it whatever the request does.
 */
export function flushSuggestedTaskAck(): void {
  const label = pendingLabel;
  pendingLabel = undefined;
  if (!label) return;
  void apiFetch(`${API_BASE}/api/mark-suggested-task-acted`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ label }),
  }).catch(() => {
    // Deliberately swallowed — see above.
  });
}

/** Test seam: the label currently parked, if any. */
export function pendingSuggestedTaskLabel(): string | undefined {
  return pendingLabel;
}
